/**
 * Frontend PII Decryption
 * Decrypts PII fields received from backend in PIE: format using session piiKey.
 * Uses Web Crypto API (AES-256-GCM).
 */

let piiKey = null;

export function setPiiKey(key) {
  piiKey = key;
}

export function getPiiKey() {
  return piiKey;
}

export function isPiiEncrypted(value) {
  return value && typeof value === 'string' && value.startsWith('PIE:');
}

/**
 * Decrypt a single PIE-encrypted value
 * @param {string} ciphertext - "PIE:{iv}:{authTag}:{data}" (all base64)
 * @returns {Promise<string>} decrypted plaintext
 */
export async function decryptPiiValue(ciphertext) {
  if (!ciphertext || !piiKey || !isPiiEncrypted(ciphertext)) return ciphertext;

  try {
    const parts = ciphertext.substring(4).split(':');
    if (parts.length !== 3) return ciphertext;

    const [ivB64, authTagB64, dataB64] = parts;
    const iv = Uint8Array.from(atob(ivB64), c => c.charCodeAt(0));
    const authTag = Uint8Array.from(atob(authTagB64), c => c.charCodeAt(0));
    const encrypted = Uint8Array.from(atob(dataB64), c => c.charCodeAt(0));

    // Combine encrypted data + authTag (GCM expects them concatenated)
    const combined = new Uint8Array(encrypted.length + authTag.length);
    combined.set(encrypted);
    combined.set(authTag, encrypted.length);

    // Import key
    const keyBytes = new Uint8Array(piiKey.match(/.{1,2}/g).map(byte => parseInt(byte, 16)));
    const cryptoKey = await crypto.subtle.importKey('raw', keyBytes, { name: 'AES-GCM' }, false, ['decrypt']);

    // Decrypt
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, cryptoKey, combined);
    return new TextDecoder().decode(decrypted);
  } catch (error) {
    console.error('PII decryption failed:', error);
    return ciphertext;
  }
}

/**
 * Decrypt all PII fields in an object
 * @param {Object} item - object with PIE-encrypted fields
 * @returns {Promise<Object>} object with decrypted fields
 */
export async function decryptPiiFields(item) {
  if (!item || !piiKey) return item;

  const result = { ...item };
  for (const key of Object.keys(result)) {
    if (isPiiEncrypted(result[key])) {
      result[key] = await decryptPiiValue(result[key]);
    }
  }
  return result;
}
